/**
 * Background effects on the **pre-join preview** (SC-158).
 *
 * The device-selection dialog shows a raw `getUserMedia` stream, not a LiveKit
 * publication, so none of the in-call machinery applies: there is no room, no
 * `LocalVideoTrack`, and no publish/republish cycle to hook. This drives the
 * processor directly instead, which is the same thing `LocalTrack.setProcessor`
 * does internally — build a hidden element for the transformer to read frames
 * from, and hand back the processed output.
 *
 * It deliberately returns a **stream** rather than writing to a video element.
 * Two surfaces show the preview (the card and the effects dialog), and letting
 * each bind the stream itself keeps React in charge of what is on screen —
 * where an element-mutating version had to race against whoever else touched
 * `srcObject`.
 *
 * Two constraints shape the rest:
 *
 *  - **One processor at a time, and never past the handoff.** Each processor
 *    holds a WebGL context, and the page has a hard ceiling on those; exceeding
 *    it makes the browser evict the least-recently-used context, often the
 *    Phaser game canvas. `stop()` must therefore run before the call pipeline
 *    builds its own processor, not merely "eventually". The one exception is
 *    the step-down to the lighter segmentation model: the new processor has to
 *    be running before the old one is dropped, or the preview would show a gap
 *    (or worse), so for the seconds the lighter one takes to start there are two
 *    (and the in-call dialog's, if it is open, makes three). It is a one-off per
 *    page, and the old one cannot be throttled meanwhile: the library has no
 *    pause, and passing frames through would show the raw camera.
 *  - **A failure is never the raw camera.** With an effect selected, the user
 *    asked not to be seen against their room — in the preview as much as in the
 *    call. Every path that cannot produce a processed stream says *why*, and
 *    the caller keeps the preview covered and explains it.
 */
import {
  createBackgroundProcessor,
  createSlowStreakTracker,
  destroyBackgroundProcessor,
  evaluateBackgroundHealth,
  isBackgroundEffectAvailable,
  isProcessorOnSteppedDownModel,
  preloadBackgroundImage,
  prewarmBackgroundAssets,
  prewarmFallbackSegmentationModel,
  resolveBackgroundImage,
  stepDownSegmentation,
  toProcessorOptions,
  type BackgroundEffect,
  type BackgroundFrameStats,
  type BackgroundProblemCause,
} from './liveKitVideoBackground'
import {
  createFrameCounter,
  startProcessorOnTrack,
  type FrameCounter,
  type StartedProcessor,
} from './processorStartup'

/** Why the preview could not show the chosen effect. */
export type PreviewFailureCause = Extract<
  BackgroundProblemCause,
  'gone' | 'unavailable' | 'processor-failed' | 'unsupported'
>

export interface PreviewSwitchResult {
  ok: boolean
  /** A newer switch replaced this one; it owns the outcome. */
  superseded?: boolean
  failure?: PreviewFailureCause
  /**
   * After a failure: whether the processor is still showing what it showed
   * before. False means it is in an unknown state and should be rebuilt.
   */
  restored?: boolean
}

export interface PreviewSwitchOptions {
  /**
   * Ask the server whether the image still exists. Default true: a switch is an
   * apply. False only to put back an effect that was on screen moments ago
   * (R9-N1): it needs no deletion check, and a restore that waits on the
   * network that just failed the pick it is undoing would fail too.
   */
  revalidate?: boolean
}

export interface PreviewBackgroundHandle {
  /**
   * The processed stream to display. Replaced by a new one, once, if the page
   * steps down to the lighter segmentation model ({@link stepDownSegmentation});
   * {@link AttachPreviewBackgroundOptions.onStreamReplaced} says when. Read it
   * again then rather than keeping this one.
   */
  stream: MediaStream
  /**
   * Change the effect on the **existing** processor.
   *
   * The processed track keeps flowing, so the previous effect stays on screen
   * until the new one is in: the image is downloaded and decoded first, and the
   * patched library (patches/@livekit+track-processors) swaps it in atomically. Tearing the processor down and building
   * another would swap the stream, and the element would show black — or,
   * worse, the raw camera — in the gap.
   *
   * `'none'` is a legitimate switch, not a failure: the processor stays alive in
   * pass-through mode. That keeps turning an effect off smooth — the displayed
   * track never changes, so the video element never resets to a black frame the
   * way swapping `srcObject` does.
   *
   * From the pass-through to an effect it resolves only once a frame has been
   * composited with the effect, so the caller can keep the raw view uncovered
   * until then and no longer.
   */
  switchTo: (
    effect: BackgroundEffect,
    options?: PreviewSwitchOptions
  ) => Promise<PreviewSwitchResult>
  /** Release the processor and its WebGL context. Safe to call more than once. */
  stop: () => void
}

/**
 * How long a started processor may take to produce its first processed frame
 * before the attach counts as failed. The clock starts once `init()` and the
 * input element's `play()` have resolved, as the call's does after
 * `setProcessor`: the cold WASM, model and image load is `init()`'s, bounded
 * separately by {@link PROCESSOR_START_TIMEOUT_MS}, and a slow link must not
 * use up the warm-up's time.
 */
const FIRST_FRAME_TIMEOUT_MS = 10_000

/**
 * How many times a preview that is merely busy tries to take the lighter model
 * before it stays where it is. A model that never loads would otherwise build a
 * processor beside the running one every few seconds, for as long as the Join
 * screen is open.
 */
const MAX_QUIET_STEP_DOWN_TRIES = 3

export interface AttachPreviewBackgroundOptions {
  /** The raw camera track from the preview stream. */
  sourceTrack: MediaStreamTrack
  effect: BackgroundEffect
  /**
   * Abandons the attach mid-flight.
   *
   * `init()` loads WASM, a segmentation model and possibly an image, so it is
   * easily slow enough for the user to pick a different effect before it
   * finishes. Without this the caller has no handle to stop yet, and the
   * abandoned processor would keep its WebGL context for the life of the page.
   */
  signal?: AbortSignal
  /** Called once if the device cannot keep up with the effect. */
  onSlow?: () => void
  /**
   * Called with the new stream when the step-down replaces the processor.
   *
   * A new stream, not a track swapped inside the old one, so that what is bound
   * to it is bound again the way it is for any new stream: the surfaces play it
   * and cover the preview until it has painted, which a swap inside a stream
   * they already hold would not give them (Safari leaves an element paused
   * after one).
   */
  onStreamReplaced?: (stream: MediaStream) => void
  /**
   * Settles once the processor may start: the caller's capture-resolution
   * raise. The processor follows a frame-size change, but a resize clears its
   * mask to background, so the person is gone until the next segmentation
   * lands. Started after the raise, it never resizes as the preview appears;
   * the image is fetched while this runs, not after it.
   */
  beforeInit?: () => Promise<void>
}

export interface PreviewAttachResult {
  handle?: PreviewBackgroundHandle
  failure?: PreviewFailureCause
}

/** Settles true once `url` is loaded and decoded, false if it will not load. */
const preloaded = (url: string): Promise<boolean> =>
  preloadBackgroundImage(url).then(
    () => true,
    () => false
  )

/** The image an effect needs, resolved and decoded, or why it cannot be had. */
async function resolveEffectOptions(
  effect: BackgroundEffect,
  { revalidate = true }: PreviewSwitchOptions = {}
): Promise<{ options?: ReturnType<typeof toProcessorOptions>; failure?: PreviewFailureCause }> {
  // The cached image is decoded while the server is asked whether it still
  // exists, rather than after: one after the other, the round-trip and the
  // decode add up to the wait the user sits through on every pick. The cached
  // lookup goes first, so with nothing cached the revalidation joins its
  // request instead of starting a second one.
  const cached = resolveBackgroundImage(effect, { revalidate: false })
  // `revalidate`: the preview is an apply too. The object-URL cache lives for
  // the page, so without it a background deleted while the dialog was closed
  // would come straight back. The decode above never stands in for this answer.
  const fresh = revalidate ? resolveBackgroundImage(effect, { revalidate: true }) : cached
  // Awaited below either way; this only keeps a rejection while `cached` is
  // awaited from surfacing as unhandled.
  void fresh.catch(() => undefined)

  const early = await cached
  const decoding = early.url ? preloaded(early.url) : null
  const { url, gone } = await fresh
  if (gone) return { failure: 'gone' }
  const options = toProcessorOptions(effect, url)
  // `disabled` is the pass-through — the raw camera. Asked for an effect, that
  // only means its image could not be had.
  if (effect.kind !== 'none' && options.mode === 'disabled') return { failure: 'unavailable' }
  if (url) {
    // The library keeps one object URL per image, so this is the URL decoded
    // above; anything else is decoded now.
    const decoded = url === early.url && decoding ? await decoding : await preloaded(url)
    if (!decoded) return { failure: 'unavailable' }
  }
  return { options }
}

/**
 * Build a processed preview for `effect`.
 *
 * `null` when there is nothing to build — "no effect", a track that has already
 * ended, or an attach the caller abandoned. Otherwise a handle, or the reason
 * there is none.
 */
export async function attachPreviewBackgroundEffect({
  sourceTrack,
  effect,
  signal,
  onSlow,
  onStreamReplaced,
  beforeInit,
}: AttachPreviewBackgroundOptions): Promise<PreviewAttachResult | null> {
  if (effect.kind === 'none') return null
  if (!isBackgroundEffectAvailable()) return { failure: 'unsupported' }
  if (sourceTrack.readyState !== 'live') return null
  // The first pick starts the download of the WASM and the model now, not when
  // init() asks for them: init() is timed, and a cold download of the default
  // model is most of what it waits for. A no-op after the first call.
  prewarmBackgroundAssets(effect)

  let initial: Awaited<ReturnType<typeof resolveEffectOptions>>
  try {
    initial = await resolveEffectOptions(effect)
  } catch {
    initial = { failure: 'unavailable' }
  }
  if (signal?.aborted) return null
  if (initial.failure) return { failure: initial.failure }
  if (beforeInit) {
    // A raise that failed leaves the resolution as it is, which init() reads.
    await beforeInit().catch(() => undefined)
    // Before anything is built, so there is nothing to tear down.
    if (signal?.aborted || sourceTrack.readyState !== 'live') return null
  }

  // Frame bookkeeping, for the first-frame wait and the slow-device warning.
  const recentFrames: BackgroundFrameStats[] = []
  let slowReported = false
  /**
   * The frames of the processor on screen. A processor built to replace it (the
   * step-down) counts into its own until it takes over: those frames say
   * nothing about the picture on screen, and the old one's must not judge it
   * once it has been replaced.
   */
  let frames: FrameCounter = createFrameCounter()
  /**
   * A step-down is queued or running. While it is, the processor on screen is
   * not judged: it is about to be replaced, and a notice raised about it would
   * outlive the swap that answers it.
   */
  let stepDownPending = false
  /**
   * `quiet`: the step-down answers a machine that is busy rather than failing
   * (a sustained `reduce-quality`), so it must not end in the slow notice when
   * the rebuild does not happen.
   */
  let queueStepDown: (quiet: boolean) => void = () => undefined
  const slowStreak = createSlowStreakTracker()
  let quietRebuildsFailed = 0
  const frameHandlerFor = (counter: FrameCounter) => (stats: BackgroundFrameStats) => {
    counter.record()
    if (counter !== frames || stepDownPending) return
    recentFrames.push(stats)
    if (recentFrames.length > 120) recentFrames.splice(0, recentFrames.length - 120)
    const health = evaluateBackgroundHealth(recentFrames)
    const sustained = slowStreak.observe(health)
    // A busy streak may end in the lighter model: have it in the cache first.
    if (slowStreak.streaking) prewarmFallbackSegmentationModel()
    if (slowReported || (health !== 'disable' && !sustained)) return
    const quiet = health !== 'disable'
    // A busy machine that has refused the rebuild MAX times is left where it is.
    // A later switchTo re-arms the streak, and without this it would build one
    // more processor, which fails the same way, on every effect pick.
    if (quiet && quietRebuildsFailed >= MAX_QUIET_STEP_DOWN_TRIES) return
    // The first slow verdict on a page is answered by the lighter model and
    // no notice, and so is a processor built before the page stepped down
    // (the call may have done it): only a device that is slow on the lighter
    // model too is told, and only when it is slow, not merely busy.
    if (stepDownSegmentation() || isProcessorOnSteppedDownModel(active?.processor)) {
      slowReported = true
      stepDownPending = true
      queueStepDown(quiet)
    } else if (!quiet) {
      slowReported = true
      onSlow?.()
    }
  }

  // What the processor is showing, and the order its calls run in. Held up here
  // because the step-down queues behind the switches already asked for.
  let currentOptions = initial.options
  /**
   * Latest wins. Resolving an image is a network round-trip and blur is not,
   * so an earlier switch to an image could finish after a later switch to
   * "none" and put the image back over the user's newer choice. Each switch
   * takes a ticket; one that is no longer the latest after its await stands
   * down, and the processor calls themselves run one at a time and in order.
   */
  let latestSwitch = 0
  let switchQueue: Promise<unknown> = Promise.resolve()

  // Constructed in the target mode so the image is loaded inside init(), before
  // the first frame.
  const processor = createBackgroundProcessor({
    initial: initial.options,
    onFrameProcessed: frameHandlerFor(frames),
  })
  if (!processor) return { failure: 'unsupported' }

  /** The processor on screen, once it has been started; replaced by the step-down. */
  let active: StartedProcessor | null = null
  /** Lets go of a step-down processor that is still being brought up. */
  let discardRebuild: (() => void) | null = null
  let stopped = false
  /** The processed stream handed back; replaced by the step-down. */
  let stream: MediaStream | null = null
  /**
   * Settles once there is a stream to replace, or the attach is over. Frames
   * flow from the end of `init()`, a little before the stream exists, and a slow
   * verdict must not find nothing to work on.
   */
  let markReady: () => void = () => undefined
  const ready = new Promise<void>((resolve) => {
    markReady = resolve
  })
  /** Either the handle was stopped, or the caller abandoned this attach. */
  const cancelled = () => stopped || signal?.aborted === true
  const stop = () => {
    if (stopped) return
    stopped = true
    markReady()
    discardRebuild?.()
    // Not yet started only if init() threw before returning: nothing else to release.
    if (active) active.release()
    else void destroyBackgroundProcessor(processor)
  }

  /**
   * Replace the processor with one built on the lighter model, after the page
   * has stepped down.
   *
   * The new one is brought all the way up beside the old one, and the stream is
   * replaced only once it has composited a frame, so the preview never shows a
   * gap or the raw camera: the old processor renders until then. A failed
   * rebuild leaves the old processor running; the caller then says it is slow.
   */
  const swapInSteppedDownProcessor = async (): Promise<boolean> => {
    const nextFrames = createFrameCounter()
    const next = createBackgroundProcessor({
      initial: currentOptions,
      onFrameProcessed: frameHandlerFor(nextFrames),
    })
    if (!next) return false

    const start = startProcessorOnTrack(next, sourceTrack)
    const discard = () => {
      if (discardRebuild === discard) discardRebuild = null
      start.release()
    }
    discardRebuild = discard

    try {
      if ((await start.outcome) !== 'started' || stopped) {
        discard()
        return false
      }
      await start.feed()
      // A pass-through composites nothing, and so reports no frames.
      if (
        currentOptions.mode !== 'disabled' &&
        nextFrames.seen === 0 &&
        !(await nextFrames.wait(1, FIRST_FRAME_TIMEOUT_MS))
      ) {
        discard()
        return false
      }
      const nextTrack = next.processedTrack
      if (!nextTrack || stopped) {
        discard()
        return false
      }

      const previous = active
      stream = new MediaStream([nextTrack])
      active = start
      frames = nextFrames
      discardRebuild = null
      // Judged afresh: its frames are its own, and if it is slow too, the
      // notice is the answer.
      recentFrames.length = 0
      slowStreak.reset()
      slowReported = false
      previous?.release()
      onStreamReplaced?.(stream)
      return true
    } catch (error) {
      console.warn('[SC-377] preview could not step down to the lighter model:', error)
      discard()
      return false
    }
  }

  queueStepDown = (quiet) => {
    switchQueue = switchQueue
      .then(async () => {
        await ready
        let swapped = false
        if (!stopped) swapped = await swapInSteppedDownProcessor()
        stepDownPending = false
        if (stopped || swapped) return
        if (quiet) {
          // Busy, not failing: no notice. A later `disable` still gets its own.
          slowReported = false
          // The streak that asked for this has fired and would never again, so
          // a preview whose rebuild failed once stayed on the heavier model for
          // good. Start it over, so a window that is still busy can try again;
          // but not for ever, since every try builds a processor beside the old
          // one, which is a lot of work for a machine that is already hot.
          quietRebuildsFailed += 1
          if (quietRebuildsFailed < MAX_QUIET_STEP_DOWN_TRIES) slowStreak.reset()
          return
        }
        // Slow, and not rebuilt: today's behaviour, the notice.
        slowReported = true
        onSlow?.()
      })
      .catch(() => {
        stepDownPending = false
      })
  }

  try {
    active = startProcessorOnTrack(processor, sourceTrack, signal)
    const started = await active.outcome

    // A stop() or an abort racing the init above must win: returning now would
    // leak this processor's WebGL context for the life of the page.
    if (cancelled()) {
      stop()
      return null
    }
    if (started === 'timed-out') {
      console.warn('[SC-158] preview background processor did not start in time')
      stop()
      return { failure: 'processor-failed' }
    }

    await active.feed()
    // From here it is the pipeline's warm-up, not the download.
    const firstFrame =
      frames.seen > 0 ? Promise.resolve(true) : frames.wait(1, FIRST_FRAME_TIMEOUT_MS)

    const processedTrack = processor.processedTrack
    if (!processedTrack) throw new Error('processor produced no track')
    if (cancelled()) {
      stop()
      return null
    }
    stream = new MediaStream([processedTrack])
    markReady()

    /**
     * Whether a frame composited with `currentOptions` has been seen since the
     * processor last left the pass-through. Not the same as "not on the
     * pass-through": a switch whose frame wait timed out has already handed
     * the library its options, yet the last frame on screen may still be a
     * pass-through one, the raw camera. A switch queued behind it must wait for
     * its own frame too, or it reports ok over that frozen frame (R13-N2).
     * The attach proved its own first frame before handing the stream back.
     */
    let effectFrameProven = initial.options.mode !== 'disabled'

    const switchTo = async (
      next: BackgroundEffect,
      switchOptions?: PreviewSwitchOptions
    ): Promise<PreviewSwitchResult> => {
      if (stopped) return { ok: false, failure: 'processor-failed', restored: false }
      const ticket = ++latestSwitch
      const superseded = () => ticket !== latestSwitch

      let resolved: Awaited<ReturnType<typeof resolveEffectOptions>>
      try {
        resolved = await resolveEffectOptions(next, switchOptions)
      } catch {
        resolved = { failure: 'unavailable' }
      }
      if (superseded()) return { ok: !stopped, superseded: true }
      // Nothing on the processor has changed: the previous effect is intact.
      if (resolved.failure) return { ok: false, failure: resolved.failure, restored: true }
      const nextOptions = resolved.options

      const run = switchQueue.then(async (): Promise<PreviewSwitchResult> => {
        if (stopped) return { ok: false, failure: 'processor-failed', restored: false }
        if (superseded()) return { ok: true, superseded: true }
        try {
          await active.processor.switchTo(nextOptions)
        } catch (error) {
          console.warn('[SC-158] preview effect switch failed:', error)
          if (superseded()) return { ok: !stopped, superseded: true }
          try {
            await active.processor.switchTo(currentOptions)
            return { ok: false, failure: 'processor-failed', restored: true }
          } catch {
            return { ok: false, failure: 'processor-failed', restored: false }
          }
        }
        currentOptions = nextOptions
        // A new effect is judged on its own frames, and warned about on its own,
        // as the call does when an effect is applied: the previous effect's slow
        // frames say nothing about this one, and a lighter pick after a warning
        // must be able to earn its own.
        recentFrames.length = 0
        slowStreak.reset()
        slowReported = false
        if (nextOptions.mode === 'disabled') {
          effectFrameProven = false
          return { ok: !stopped }
        }
        // From the pass-through, the raw camera stays on screen until the
        // effect is in, and the caller shows it uncovered for exactly that
        // long. So "done" is the first frame composited with the effect, not
        // the library taking the options: the frame the pipeline is on when
        // the switch lands was still read under the old ones. Frames are
        // serialized, so the next one counted is the first with the new
        // options.
        if (!effectFrameProven) {
          const composited = await frames.wait(1, FIRST_FRAME_TIMEOUT_MS)
          if (!composited || stopped) {
            // Stalled on the effect: nothing the caller may keep on screen.
            console.warn('[SC-158] preview effect produced no frame after the switch')
            return { ok: false, failure: 'processor-failed', restored: false }
          }
          effectFrameProven = true
        }
        return { ok: !stopped }
      })
      switchQueue = run.catch(() => undefined)
      return run
    }

    // A processor that never produces a frame is a processor that did not
    // start: handing its stream back would leave a black preview for good.
    const produced = await firstFrame
    if (cancelled()) {
      stop()
      return null
    }
    if (!produced) {
      stop()
      return { failure: 'processor-failed' }
    }

    return {
      handle: {
        get stream() {
          return stream
        },
        switchTo,
        stop,
      },
    }
  } catch (error) {
    console.warn('[SC-158] preview background effect could not start:', error)
    stop()
    return cancelled() && signal?.aborted ? null : { failure: 'processor-failed' }
  }
}

export default attachPreviewBackgroundEffect
